import { useCallback, useEffect } from "react";
import { useBrowserWallet } from "@/features/wallet";
import { useAuthStore } from "../../../store/auth-store";
import { authAPI } from "../../../api/auth";
import { useToast } from "../../../hooks/useToast";

/**
 * UseAuth Hook Return Type
 */
export interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  wallet: string | null;
  token: string | null;
  error: string | null;
  logout: () => Promise<void>;
}

/**
 * useAuth Hook
 *
 * Manages authentication state and provides login/logout functionality
 * for Solana wallets using challenge-response authentication.
 *
 * Authentication Flow:
 * 1. Connect to Solana wallet via Jupiter Unified Wallet
 * 2. Request challenge message from backend
 * 3. Sign challenge message with wallet
 * 4. Verify signature with backend and receive JWT token
 * 5. Store token in localStorage and memory
 *
 * Note: For the interactive login flow (modal + signature dialog), use
 * `useAuthModal` from `./useAuthModal`. This hook only exposes auth state
 * and the logout helper.
 *
 * @example
 * ```tsx
 * const { isAuthenticated, logout, isLoading, error } = useAuth();
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error}</div>;
 *
 * return (
 *   <div>
 *     {isAuthenticated ? (
 *       <button onClick={logout}>Logout</button>
 *     ) : (
 *       <button onClick={() => openLoginModal()}>Login with Solana</button>
 *     )}
 *   </div>
 * );
 * ```
 */
export function useAuth(): UseAuthReturn {
  const { success, error: showError } = useToast();
  const { disconnect, connected } = useBrowserWallet();
  const {
    isAuthenticated,
    isLoading,
    wallet,
    token,
    error,
    setLoading,
    setError,
    clearAuth,
    isTokenExpired,
    setLastVerified,
    shouldVerifyToken,
  } = useAuthStore();

  /**
   * Auto-load and verify token on app initialization
   * Only verifies if token exists and hasn't been verified recently
   */
  useEffect(() => {
    const initAuth = async () => {
      // Check if token is expired locally first (cheap check)
      if (token && isTokenExpired()) {
        clearAuth();
        showError("Session Expired", {
          description: "Your session has expired. Please sign in again.",
        });
        return;
      }

      // Only verify with backend if we should (prevents unnecessary API calls)
      if (!shouldVerifyToken()) {
        return;
      }

      // Verify token with backend
      if (token && wallet) {
        try {
          const result = await authAPI.verifyToken(token);

          if (result.valid) {
            // Token is valid, update last verification timestamp
            setLastVerified(Date.now());
          } else {
            // Token is invalid, clear auth state
            clearAuth();
            showError("Session Expired", {
              description: "Please sign in again.",
            });
          }
        } catch (err) {
          console.error("Token verification failed:", err);

          // Only clear auth on non-network errors
          // Network errors might be transient, so keep the token
          const errorMessage = err instanceof Error ? err.message : "";
          const isNetworkError =
            errorMessage.includes("timeout") ||
            errorMessage.includes("network") ||
            errorMessage.includes("fetch");

          if (!isNetworkError) {
            // Server returned an error (401, 403, etc), token is invalid
            clearAuth();
            showError("Session Invalid", {
              description: "Please sign in again.",
            });
          } else {
            // Network error, log but don't clear auth yet
            console.warn(
              "Network error during token verification, will retry later",
            );
          }
        }
      }
    };

    initAuth();
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Logout function - invalidates JWT session and disconnects wallet
   */
  const logout = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Call logout API if we have a token
      if (token) {
        try {
          await authAPI.logout(token);
        } catch (err) {
          // Log but don't fail on API error - we'll still clear local state
          console.warn("Logout API call failed:", err);
        }
      }

      // Disconnect wallet via Jupiter
      if (connected) {
        try {
          await disconnect();
        } catch (err) {
          // Log but don't fail on disconnect error
          console.warn("Wallet disconnect failed:", err);
        }
      }

      // Clear local auth state
      clearAuth();

      success("Logged Out", {
        description: "You have been successfully logged out.",
      });
    } catch (err) {
      console.error("Logout failed:", err);

      // Clear local state even if something fails
      clearAuth();

      const errorMessage = err instanceof Error ? err.message : "Logout failed";
      success("Logout Warning", {
        description: `${errorMessage}. Local session cleared.`,
      });
    } finally {
      setLoading(false);
    }
  }, [
    token,
    connected,
    disconnect,
    clearAuth,
    setLoading,
    setError,
    success,
    showError,
  ]);

  return {
    isAuthenticated,
    isLoading,
    wallet,
    token,
    error,
    logout,
  };
}
